import React, { Suspense, useRef, useEffect, useState } from 'react'
import { Layout, Button, Dropdown, message } from 'antd'
import loadable from '@loadable/component'
import { getTempEnvParam } from 'env'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { isGlobal } from 'conf'
import { featureFlags } from 'config/feature-flags'
import { openloadingAni, errorBase } from 'utils/ui'

import TopBar from 'components/cleanui/layout/TopBar'
import Menu from 'components/cleanui/layout/Menu'
import RecordMenu from 'pages/records/components/RecordMenu'
import UserService from 'api/Users'
import { QUOTA_MODE } from 'types/utils'
import plusIcon from '@/assets/images/icon-create-plus.svg'

import styles from './style.module.scss'
// import WorkspaceSelect from './components/WorkspaceSelect'

// BlockNote 에디터 번들이 무거워 메모 팝업을 열 때만 로드한다
const MemoEditorModal = loadable(() => import('pages/records/components/MemoEditorModal'))

const mapStateToProps = ({ settings, menu, user }) => ({
  disableCreate: menu.disableCreate,
  isContentMaxWidth: settings.isContentMaxWidth,
  isAppMaxWidth: settings.isAppMaxWidth,
  isGrayBackground: settings.isGrayBackground,
  isSquaredBorders: settings.isSquaredBorders,
  isCardShadow: settings.isCardShadow,
  isBorderless: settings.isBorderless,
  isTopbarFixed: settings.isTopbarFixed,
  isGrayTopbar: settings.isGrayTopbar,
  // workspaceId: user.workspace.id,
  license: user.paidLicense,
})

const MainLayout = ({
  children,
  disableCreate,
  isContentMaxWidth,
  isAppMaxWidth,
  isGrayBackground,
  isSquaredBorders,
  isCardShadow,
  isBorderless,
  isTopbarFixed,
  isGrayTopbar,
  license,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // 메모 모달은 닫힐 때 unmount 되므로, 저장 성공 토스트가 사라지지 않도록
  // messageApi 를 레이아웃에서 만들어 prop 으로 내린다 (RecordEdit 의 messageApi prop 패턴)
  const [messageApi, messageContextHolder] = message.useMessage()

  const openItem = () => {
    const tablePage = getTempEnvParam('curTablePage')

    dispatch({
      type: 'settings/SET_STATE',
      payload: {
        isMobileMenuOpen: false,
      },
    })

    if (tablePage != null) {
      // tablePage.openItem(null)

      // setTimeout(() => {
      tablePage.openItem('create', null)
      // }, 100)
    }
  }

  // 리소스 quota(글로벌향 RESOURCE 모드) 초과 여부를 생성 UI 를 열기 전에 확인한다.
  // 초과면 에러 팝업만 띄우고 false 를 반환 — 호출자는 생성 UI 를 열지 않는다.
  // quota 조회 자체가 실패하면 생성을 막지 않는다(서버가 최종 검증하므로 오차단 방지).
  // 안내 문구는 패스워드/메모를 구분하지 않고 "리소스"로 통일 — 쿼타 자체가 리소스 공용 한도라
  // 생성 대상별 문구를 따로 두면 "메모 한도"처럼 실제와 다른 안내가 된다.
  const checkResourceQuota = async () => {
    if (!isGlobal() || license?.quota_mode !== QUOTA_MODE.RESOURCE) return true

    try {
      const res = await UserService.getMyResourceQuota()

      const {
        user_quota: userQuota,
        workspace_quota: workspaceQuota,
        owner_info: ownerInfo,
      } = res.data

      const isUserQuotaExceeded = userQuota.current_resources >= userQuota.max_resources
      const isWorkspaceQuotaExceeded =
        workspaceQuota.current_resources >= workspaceQuota.max_resources

      if (isUserQuotaExceeded || isWorkspaceQuotaExceeded) {
        const workspaceOwnerEmail = ownerInfo.email
        const quotaType = isUserQuotaExceeded ? t('common.quota.user') : t('common.quota.workspace')

        errorBase({
          icon: 'red',
          title: t('record.title.maxLimitReached'),
          content: t('record.desc.maxLimitReached', { quotaType, workspaceOwnerEmail }),
          width: 400,
          okText: t('common.button.ok'),
          onOk: () => {},
        })
        return false
      }
    } catch (error) {
      console.error('Error fetching resource quota:', error)
    }

    return true
  }

  // 패스워드 생성 패널 오픈. quota 는 진입 지점(드롭다운 트리거 = 생성 버튼)에서 이미 확인했으므로
  // 항목 클릭 시에는 다시 조회하지 않는다 — 같은 클릭 흐름에서 quota API 가 두 번 나가던 것 제거.
  const handleCreatePassword = () => {
    const tablePage = getTempEnvParam('curTablePage')

    // 레코드 목록(SearchTablePage)이 마운트되지 않은 화면에선 열 대상이 없다 —
    // 가드 없이 openItemType 접근 시 TypeError 크래시 (openItem() 의 null 가드와 동일 사유)
    if (tablePage == null) return

    if (tablePage.openItemType !== 'create') {
      openItem()
    }
  }

  // 메모 생성 팝업은 세션 상태만 가진다 — 새로고침하면 닫힌 상태로 시작한다
  // (URL mode 파라미터로 복원하던 동작은 제거됨)
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false)

  // 메모 에디터 청크(BlockNote+Mantine, 대형)를 유휴 시간에 미리 받아둔다 —
  // lazy 청크를 클릭 시점에 받으면 팝업이 늦게 뜨므로 첫 오픈 체감 지연 제거.
  // flag OFF 빌드에선 메모 진입 경로가 없으므로 무거운 청크를 미리 받지 않는다.
  // (테스트의 jest.mock 컴포넌트엔 preload 가 없어 옵셔널 호출)
  useEffect(() => {
    if (!featureFlags.memoTemplate) return undefined
    const preload = () => MemoEditorModal.preload?.()
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(preload, { timeout: 5000 })
      return () => window.cancelIdleCallback(idleId)
    }
    const timerId = setTimeout(preload, 3000)
    return () => clearTimeout(timerId)
  }, [])

  // 브라우저 기본 "페이지 저장"(Cmd/Ctrl+S) 차단. 저장 형식(전체/HTML만/단일파일)은 저장
  // 대화상자에서 사용자만 고를 수 있어 페이지가 강제할 수 없고, 기본값 "전체"로 저장되면
  // HTML 과 함께 _files 폴더에 SVG·JS 번들·폰트까지 내려간다. 앱 화면에서 의도 없이 눌리는
  // 이 경로만 없앤다 — 브라우저 메뉴·우클릭·Cmd+P 는 브라우저 UI 라 웹페이지가 막을 수 없으므로
  // 보안 통제가 아니라 실수 방지용이다(공개/로그인 화면까지 기본 동작을 빼앗지 않도록
  // 전역 layouts/index.js 가 아니라 인증 구간인 이 레이아웃에 둔다).
  // capture 단계로 등록해 내부 핸들러의 stopPropagation 보다 먼저 잡는다. propagation 은
  // 막지 않으므로 메모 에디터의 저장 단축키(자체 리스너)는 이어서 그대로 실행된다.
  useEffect(() => {
    const handleSaveShortcut = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return
      // Cmd/Ctrl 조합에서는 한글 IME 입력 모드여도 브라우저가 key 를 라틴 문자로 준다
      if (event.key?.toLowerCase() !== 's') return
      event.preventDefault()
    }
    window.addEventListener('keydown', handleSaveShortcut, true)
    return () => window.removeEventListener('keydown', handleSaveShortcut, true)
  }, [])

  // 메모 에디터 팝업 오픈. 패스워드와 마찬가지로 quota 는 생성 버튼(트리거)에서 이미 확인했다.
  const handleCreateMemo = () => {
    // 이미 메모 생성 팝업이 떠 있으면 무시 — 상태가 흔들리지 않게 명시 가드
    if (isMemoModalOpen) return

    setIsMemoModalOpen(true)
  }

  const handleCloseMemo = () => {
    setIsMemoModalOpen(false)
  }

  // 생성 드롭다운 열림 상태 — quota 초과 시 메뉴를 열지 않으려면 controlled 여야 한다
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false)

  // 생성 드롭다운 항목(패스워드/메모). 드롭다운은 memoTemplate flag ON 일 때만 렌더되므로
  // (아래 preElem 참고) 메모 항목은 항상 포함한다.
  const createMenuItems = [
    { key: 'password', label: t('appConfig.menu.createPassword') },
    { key: 'memo', label: t('appConfig.menu.createMemo') },
  ]

  const handleCreateMenuClick = ({ key }) => {
    // controlled 드롭다운이라 항목 선택 후 닫는 것도 직접 처리
    setIsCreateMenuOpen(false)
    if (key === 'password') {
      handleCreatePassword()
    } else if (key === 'memo') {
      handleCreateMemo()
    }
  }

  // 생성 버튼(드롭다운 트리거)을 누른 시점에 quota 를 확인한다 — 초과면 메뉴를 열지 않고
  // 에러 팝업만 띄워, 항목을 고른 뒤에야 막히는 흐름을 없앤다(패스워드·메모 공통).
  // quota 조회가 도는 조합(글로벌향 + RESOURCE 모드)에서만 await 가 실제 지연이고
  // 그 외에는 즉시 통과하므로 메뉴 오픈 체감은 그대로다.
  const handleCreateMenuOpenChange = async (open) => {
    if (!open) {
      setIsCreateMenuOpen(false)
      return
    }
    // 여는 순간에도 메모 에디터 청크 preload — 유휴 preload 가 아직 안 돈 경우의 보험
    MemoEditorModal.preload?.()
    // quota 조회를 await 하는 동안의 중복 클릭 방지 (생성 핸들러와 같은 가드 공유)
    if (isClickingRef.current) return
    isClickingRef.current = true

    try {
      if (await checkResourceQuota()) {
        setIsCreateMenuOpen(true)
      }
    } finally {
      isClickingRef.current = false
    }
  }

  // memoTemplate flag OFF 경로(드롭다운 없는 단일 '패스워드 생성' 버튼) 전용 —
  // 트리거 시점 quota 확인(handleCreateMenuOpenChange)이 없으므로 이 버튼이 그 역할을 대신한다.
  const handleCreatePasswordOnly = async () => {
    // quota 조회를 await 하는 동안의 중복 클릭 방지
    if (isClickingRef.current) return
    isClickingRef.current = true

    try {
      if (await checkResourceQuota()) {
        handleCreatePassword()
      }
    } finally {
      isClickingRef.current = false
    }
  }

  const loading = useSelector((state) => state.user.loading)
  const loadingRef = useRef(null)
  const isClickingRef = useRef(false)

  useEffect(() => {
    if (loading) {
      loadingRef.current = openloadingAni({
        text: t('logOut.desc.progress' || 'Loading...'),
      })
    } else if (loadingRef.current) {
      loadingRef.current.destroy()
      loadingRef.current = null
    }
  }, [loading])

  const preElem = (
    <div
      className={classNames(styles.btnWrapper)}
      style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
    >
      {/* {isGlobal() && <WorkspaceSelect />} */}
      {/* memoTemplate flag 로 게이팅: ON 이면 패스워드/메모 드롭다운, OFF 면 드롭다운 없이
          '패스워드 생성' 단일 버튼(생성 패널 직접 오픈)으로 기존 동작 유지 */}
      {featureFlags.memoTemplate ? (
        <Dropdown
          menu={{ items: createMenuItems, onClick: handleCreateMenuClick }}
          trigger={['click']}
          disabled={disableCreate}
          overlayClassName="menuStyle2"
          open={isCreateMenuOpen}
          onOpenChange={handleCreateMenuOpenChange}
        >
          <Button className={classNames(styles.createBtn, 'type2', 'h48')} disabled={disableCreate}>
            <img src={plusIcon} alt="plus" />
            {t('appConfig.title.createNew')}
          </Button>
        </Dropdown>
      ) : (
        <Button
          className={classNames(styles.createBtn, 'type2', 'h48')}
          disabled={disableCreate}
          onClick={handleCreatePasswordOnly}
        >
          <img src={plusIcon} alt="plus" />
          {t('appConfig.title.createPassword')}
        </Button>
      )}
    </div>
  )

  return (
    <div className={classNames('officemail', { cui__layout__grayBackground: isGrayBackground })}>
      <Layout
        className={classNames({
          cui__layout__contentMaxWidth: isContentMaxWidth,
          cui__layout__appMaxWidth: isAppMaxWidth,
          cui__layout__grayBackground: isGrayBackground,
          cui__layout__squaredBorders: isSquaredBorders,
          cui__layout__cardsShadow: isCardShadow,
          cui__layout__borderless: isBorderless,
        })}
      >
        <Layout.Header
          className={classNames('cui__layout__header', styles.header, {
            cui__layout__fixedHeader: isTopbarFixed,
            cui__layout__headerGray: isGrayTopbar,
          })}
        >
          <TopBar />
        </Layout.Header>
        <React.Suspense fallback={<div>{t('common.title.loading')}</div>} />
        <Layout.Content className={classNames(styles.mainFrame)}>
          <Suspense fallback={<div>Loading...</div>}>
            <Layout>
              <Menu preElem={preElem} menuType="user" leftMenuComp={RecordMenu} />
              <Layout>
                <Layout.Content style={{ height: '100%', position: 'relative' }}>
                  <div className={classNames(styles.content)}>{children}</div>
                </Layout.Content>
              </Layout>
            </Layout>
          </Suspense>
        </Layout.Content>
      </Layout>
      {messageContextHolder}
      {/* 열 때마다 mount 해서 에디터 내용이 초기화되도록 조건부 렌더링 */}
      {isMemoModalOpen && <MemoEditorModal onClose={handleCloseMemo} messageApi={messageApi} />}
    </div>
  )
}

export default connect(mapStateToProps)(MainLayout)
